import styled from '@emotion/styled';
import { ArrowForward, ArrowForwardIosSharp } from '@mui/icons-material';
import {
  AccordionProps,
  AccordionSummaryProps,
  Button,
  Card,
  Divider,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import { RSSkillDrawerIcon, SkillsIcon } from '../dashboard-icon';
import image1 from 'src/pages/therapist-dashboard/Img/GameImg/falling_fruits@2x.png';
import image2 from 'src/pages/therapist-dashboard/Img/GameImg/ladybugs.png';
import image3 from 'src/pages/therapist-dashboard/Img/GameImg/lucky_match.png';
import image4 from 'src/pages/therapist-dashboard/Img/GameImg/rock_climbing_ruins.png';
import { useDispatch, useSelector } from 'react-redux';
import { getGamesByIntervention } from 'src/redux/actions/therapist-dashboard-action';
import { baseApiUrl } from 'src/config';
import { exctractSubdomain } from 'src/utils/generate-url';
type Props = { skillData: any; patientUuid: any };

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion
    disableGutters
    elevation={0}
    square
    {...props}
  />
))(({ theme }) => ({
  '&:before': {
    display: 'none',
  },
}));

const SeeAllIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="19"
      viewBox="0 0 18 19"
      fill="none"
    >
      <path
        d="M6.75 13.8828L11.25 9.38281L6.75 4.88281"
        stroke="#E81538"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const getSkillIconUrl =
  'https://play.stage.restoreskills.com/static/js/webcore/webcore-master/webcore_all_files/assets/new_functional_skill_icons';

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: '#fff',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: 1,
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: 2,
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

const RestoreSkillCard = (props: Props) => {
  const { patientUuid } = props;
  const { skillData } = props;
  const dispatch = useDispatch();
  const interventionGroup = useSelector((state: any) => state.dashboardInterventionGroupReducer);
  const gamesByInterventionData = useSelector(
    (state: any) => state.dashboardGamesInterventionReducer
  );

  const [expanded, setExpanded] = useState(null);

  const handleChange = (panel: any) => (event: any, isExpanded: any) => {
    if (isExpanded) {
      dispatch(
        getGamesByIntervention({
          interventionId: panel,
        })
      );
      setExpanded(panel);
      return;
    }
    setExpanded(null);
  };

  const [selectedSkills, setSelectedSkills] = useState<any>([]);

  const skill_game = [image1, image2, image3, image4];

  const skillsConstant: any = {
    LE_STRENGTHENING: 'LE Strengthening',
    SITTING_REACH: 'Sitting Reach',
    SIT_TO_STAND: 'Sit to Stand',
    STANDING_REACH: 'Standing Reach',
    STANDING_TOLERANCE: 'Stand Tolerance',
    SITTING_TOLERANCE: 'Sitting Tolerance',
  };

  const [currentTab, setCurrentTab] = useState<string>('');

  const handleTabsChange = (event: any, value: any) => {
    const tabObject = interventionGroup?.response?.groups?.find((tab: any) => tab?.id === value);
    setSelectedSkills(tabObject?.interventions);
    setCurrentTab(value);
  };

  const gameAPI = (interventionId: any, gameId: any) => {
    let realm = localStorage.getItem('realm');

    if (!realm) {
      realm = exctractSubdomain();
    }

    const baseGameUrl = baseApiUrl.includes('stage')
      ? 'app.stage.restoreskills.com'
      : 'app.restoreskills.com';
    const gameUrl =
      'https://' +
      realm +
      '-' +
      baseGameUrl +
      '/intervention/' +
      interventionId +
      '/' +
      patientUuid +
      '/' +
      gameId;
    window.open(gameUrl, '_blank');
  };

  useEffect(() => {
    if (
      interventionGroup?.isIntervention &&
      !!interventionGroup?.response?.groups?.length &&
      !currentTab &&
      selectedSkills?.length === 0
    ) {
      setSelectedSkills(interventionGroup?.response?.groups[0]?.interventions);
      setCurrentTab(interventionGroup?.response?.groups[0]?.id);
    }
  }, [interventionGroup]);

  return (
    <Card
      sx={{
        borderRadius: '20px',
        border: '1px solid  #E5E7EB',
        m: 2,
      }}
    >
      <Box sx={{ py: 2, px: 2, display: 'flex', gap: 2 }}>
        <RSSkillDrawerIcon />

        <Box>
          <Typography variant="h6">RESTORE Skills</Typography>
          <Typography
            sx={{ color: '#6C737F' }}
            variant="caption"
          >
            Engaging activities for therapy teams
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', mb: 2, marginLeft: '12px' }}>
        <Tabs
          indicatorColor="primary"
          onChange={handleTabsChange}
          scrollButtons="auto"
          textColor="inherit"
          value={currentTab}
          variant="scrollable"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: '#384250',
            },
          }}
        >
          {interventionGroup?.response?.groups?.map((tab: any) => (
            <Tab
              key={tab?.id}
              label={tab?.name}
              value={tab?.id}
            />
          ))}
        </Tabs>
      </Box>

      <Box>
        <div style={{ paddingLeft: 5 }}>
          {!!interventionGroup?.response?.groups?.length
            ? selectedSkills?.map((skill: any) => (
                <Accordion
                  key={skill?.id}
                  expanded={expanded === skill?.id}
                  onChange={handleChange(skill?.id)}
                >
                  <AccordionSummary
                    aria-controls={`${skill.id}-content`}
                    id={`${skill.id}-header`}
                    sx={{ display: 'flex', gap: 3, alignItems: 'center' }}
                  >
                    <img
                      src={`${getSkillIconUrl}/${skill?.icon_path}`}
                      alt=""
                      width="35"
                      height="35"
                      style={{
                        margin: '-4px 0px',
                      }}
                    />

                    <Typography
                      sx={{ marginLeft: 2 }}
                      variant="caption"
                    >
                      {/* {skillsConstant[skill]} */}
                      {skill?.name}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      pl={2}
                      sx={{
                        display: 'flex',
                        gap: 1,
                        width: '100%',
                        overflow: 'scroll',
                        '&::-webkit-scrollbar': {
                          width: '8px',
                          height: '2px',
                        },
                      }}
                    >
                      {gamesByInterventionData?.loading ? (
                        ['1', '2', '3', '4'].map((res: any) => (
                          <Skeleton
                            variant="circular"
                            width={100}
                            key={res}
                            height={100}
                          />
                        ))
                      ) : !!gamesByInterventionData?.response?.games?.length ? (
                        gamesByInterventionData?.response?.games?.map((img_src: any) => {
                          return (
                            <img
                              key={img_src?.id}
                              style={{
                                height: 100,
                                width: 100,
                                cursor: 'pointer',
                              }}
                              alt={img_src?.name}
                              src={img_src?.image_path}
                              onClick={() => gameAPI(skill?.id, img_src?.id)}
                            />
                          );
                        })
                      ) : (
                        <Typography
                          variant="body1"
                          sx={{ padding: '10px 10px' }}
                        >
                          No games are available
                        </Typography>
                      )}
                    </Box>
                    {!!gamesByInterventionData?.response?.games?.length ? (
                      <Button
                        sx={{ marginLeft: 1, color: '#E81538' }}
                        endIcon={<SeeAllIcon />}
                      >
                        See all
                      </Button>
                    ) : null}
                  </AccordionDetails>
                </Accordion>
              ))
            : null}
        </div>
      </Box>

      <Box
        sx={{
          borderTop: '1px solid  #E5E7EB',
          height: '44px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Divider />
        <Button
          sx={{ color: '#E81538' }}
          size="small"
          endIcon={<ArrowForward sx={{ color: '#E81538' }} />}
        >
          Explore more
        </Button>
      </Box>
    </Card>
  );
};

export default RestoreSkillCard;
